import React, { useEffect } from 'react';
import RouteMap from '../RouteMap';

/**
 * Component to display practice details
 */
const PracticeDetails = ({ practice, formData, alternativeOptions = [] }) => {
  // Debug logging
  useEffect(() => {
    console.log('PracticeDetails - practice:', practice);
    console.log('PracticeDetails - formData:', formData);
    console.log('PracticeDetails - alternativeOptions:', alternativeOptions);
    
    if (!practice) {
      console.error('practice is null or undefined');
    }
    
    if (!formData) {
      console.error('formData is null or undefined');
    }
  }, [practice, formData, alternativeOptions]);
  
  if (!practice) return null;
  
  return (
    <div className="mb-6">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
        <div>
          <h2 className="text-xl font-bold text-primary mb-1">{practice.name}</h2>
          <p className="text-gray-700 mb-2">
            {practice.address?.line1}
            {practice.address?.line2 ? `, ${practice.address.line2}` : ''}, 
            {practice.address?.city}, {practice.address?.postcode}
          </p>
          
          <div className="flex items-center gap-2 mb-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-accent" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
            </svg>
            <span className="text-gray-700">
              {practice.distance ? `${practice.distance} km from your location` : 'Distance not available'}
            </span>
          </div>
          
          <div className="flex items-center gap-2 mb-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-accent" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <a 
              href="#" 
              onClick={(e) => {
                e.preventDefault();
                const pricingSection = document.getElementById('treatment-pricing');
                if (pricingSection) {
                  pricingSection.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              className="text-accent hover:text-accent/80 font-medium"
            >
              View Treatment Pricing
            </a>
          </div>
          
          <div className="flex items-center gap-2 mb-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-accent" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v3.586L7.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 10.586V7z" clipRule="evenodd" />
            </svg>
            <a 
              href="#" 
              onClick={(e) => {
                e.preventDefault();
                const alternativeOptionsSection = document.getElementById('alternative-options');
                if (alternativeOptionsSection) {
                  alternativeOptionsSection.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              className="text-accent hover:text-accent/80 font-medium"
            >
              Alternative Options
            </a>
          </div>
          
          {practice.phone && (
            <div className="flex items-center gap-2 mb-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-accent" viewBox="0 0 20 20" fill="currentColor">
                <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
              </svg>
              <span className="text-gray-700">{practice.phone}</span>
            </div>
          )}
          
          {practice.email && (
            <div className="flex items-center gap-2 mb-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-accent" viewBox="0 0 20 20" fill="currentColor">
                <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
              </svg>
              <span className="text-gray-700">{practice.email}</span>
            </div>
          )}
          
          {practice.website && (
            <div className="flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-accent" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.083 9h1.946c.089-1.546.383-2.97.837-4.118A6.004 6.004 0 004.083 9zM10 2a8 8 0 100 16 8 8 0 000-16zm0 2c-.076 0-.232.032-.465.262-.238.234-.497.623-.737 1.182-.389.907-.673 2.142-.766 3.556h3.936c-.093-1.414-.377-2.649-.766-3.556-.24-.56-.5-.948-.737-1.182C10.232 4.032 10.076 4 10 4zm3.971 5c-.089-1.546-.383-2.97-.837-4.118A6.004 6.004 0 0115.917 9h-1.946zm-2.003 2H8.032c.093 1.414.377 2.649.766 3.556.24.56.5.948.737 1.182.233.23.389.262.465.262.076 0 .232-.032.465-.262.238-.234.498-.623.737-1.182.389-.907.673-2.142.766-3.556zm1.166 4.118c.454-1.147.748-2.572.837-4.118h1.946a6.004 6.004 0 01-2.783 4.118zm-6.268 0C6.412 13.97 6.118 12.546 6.03 11H4.083a6.004 6.004 0 002.783 4.118z" clipRule="evenodd" />
              </svg>
              <a href={practice.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                {practice.website.replace(/^https?:\/\//, '')}
              </a>
            </div>
          )}
        </div>
        
        <div className="md:text-right">
          {practice.openingHours && (
            <div className="mb-2">
              <h3 className="font-semibold text-gray-800 mb-1">Opening Hours</h3>
              <ul className="text-sm text-gray-700">
                {/* Define the correct order of days */}
                {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
                  .filter(day => practice.openingHours[day]) // Only include days that exist in the data
                  .map(day => (
                    <li key={day}>
                      <span className="font-medium capitalize">{day}:</span>{' '}
                      {practice.openingHours[day].isClosed 
                        ? 'Closed' 
                        : `${practice.openingHours[day].open} - ${practice.openingHours[day].close}`}
                    </li>
                  ))}
              </ul>
            </div>
          )}
          
          {practice.services && practice.services.length > 0 && (
            <div>
              <h3 className="font-semibold text-gray-800 mb-1">Services</h3>
              <div className="flex flex-wrap gap-1 justify-end">
                {practice.services.slice(0, 3).map(service => (
                  <span key={service} className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                    {service}
                  </span>
                ))}
                {practice.services.length > 3 && (
                  <span className="inline-block bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded">
                    +{practice.services.length - 3} more
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Map showing route from user location to practice */}
      <RouteMap originPostcode={formData.postcode} practice={practice} />
    </div>
  );
};

export default PracticeDetails;
